import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, CardMedia, Typography, TextField, Button, Chip, Box,
  CircularProgress, Link, makeStyles, IconButton, Tooltip, Select, MenuItem, Divider,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import RoomIcon from '@material-ui/icons/Room';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { mockedTutorService } from '../services';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 4 },
  sub: { color: '#6b7280', fontSize: 13, marginBottom: 16 },
  searchRow: { display: 'flex', gap: 12, marginBottom: 20, maxWidth: 520 },
  card: { height: '100%', display: 'flex', flexDirection: 'column' },
  media: { height: 180, backgroundColor: '#eef1f5' },
  noPhoto: { height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eef1f5', color: '#9aa3af', fontSize: 40, fontWeight: 700 },
  name: { fontWeight: 700, fontSize: 15 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 2 },
  chips: { marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 },
  chip: { height: 22 },
  rate: { marginTop: 8, fontWeight: 600, color: '#111827' },
  source: { marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24 },
  actions: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginTop: 'auto' },
  genderSelect: { fontSize: 13, minWidth: 92 },
  spacer: { flex: 1 },
}));

type MockedTutor = {
  id: string;
  name: string;
  gender?: string | null;
  picture?: string | null;
  mobile?: string | null;
  subjects: string[];
  classes?: string | null;
  area?: string | null;
  serviceAreas?: string[];
  pincode?: string | null;
  lat?: number | null;
  lng?: number | null;
  mode?: string | null;
  hourlyRate?: number | null;
  ratingAvg?: number | null;
  ratingCount?: number | null;
  years?: number | null;
  source?: string | null;
  sourceUrl?: string | null;
};

const MockedTeachersScreen: React.FC = () => {
  const classes = useStyles();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tutors, setTutors] = useState<MockedTutor[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await mockedTutorService.list(search, page, 24);
    if (res.ok) {
      setTutors(res.data?.tutors || []);
      setTotal(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const applySearch = () => { setPage(1); setSearch(input.trim()); };

  // Per-row request lock so a card's actions disable while one is in flight.
  const [busyId, setBusyId] = useState<string | null>(null);

  const locationUrl = (t: MockedTutor) => {
    if (typeof t.lat === 'number' && typeof t.lng === 'number') {
      return `https://www.google.com/maps/search/?api=1&query=${t.lat},${t.lng}`;
    }
    const q = [t.area, t.pincode, 'Guwahati'].filter(Boolean).join(' ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };
  const hasLocation = (t: MockedTutor) =>
    (typeof t.lat === 'number' && typeof t.lng === 'number') || !!t.area || !!t.pincode;
  const hasPin = (t: MockedTutor) => typeof t.lat === 'number' && typeof t.lng === 'number';

  const handleSetGender = async (t: MockedTutor, gender: 'Female' | 'Male' | 'Other') => {
    if (!gender || gender === t.gender) return;
    setBusyId(t.id);
    const res = await mockedTutorService.setGender(t.id, gender);
    setBusyId(null);
    if (res.ok) {
      setTutors((prev) => prev.map((x) => (x.id === t.id ? { ...x, gender } : x)));
    } else {
      window.alert(res.error || 'Failed to update gender');
    }
  };

  const handleDelete = async (t: MockedTutor) => {
    if (!window.confirm(`Delete "${t.name}"? This removes the tutor and its mock account. This cannot be undone.`)) return;
    setBusyId(t.id);
    const res = await mockedTutorService.remove(t.id);
    setBusyId(null);
    if (res.ok) {
      setTutors((prev) => prev.filter((x) => x.id !== t.id));
      setTotal((n) => Math.max(0, n - 1));
    } else {
      window.alert(res.error || 'Failed to delete tutor');
    }
  };

  return (
    <div>
      <Typography variant="h5" className={classes.header}>Mocked Teachers</Typography>
      <Typography className={classes.sub}>
        Scraped tutor leads for the ops team to contact & onboard. Preview only — {total} total.
      </Typography>

      <div className={classes.searchRow}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search name, subject, area, pincode…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
        />
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={applySearch}>Search</Button>
      </div>

      {loading ? (
        <Box p={6} display="flex" justifyContent="center"><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {tutors.map((t) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                <Card className={classes.card} elevation={1}>
                  {t.picture
                    ? <CardMedia className={classes.media} image={t.picture} title={t.name} />
                    : <div className={classes.noPhoto}>{(t.name || '?').charAt(0).toUpperCase()}</div>}
                  <CardContent>
                    <Typography className={classes.name}>{t.name}</Typography>
                    <Typography className={classes.meta}>
                      {[t.area, t.classes ? `Class ${t.classes}` : null, t.years ? `${t.years} yrs` : null].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                    {t.pincode ? <Typography className={classes.meta}>PIN {t.pincode}</Typography> : null}
                    <div className={classes.chips}>
                      {(t.subjects || []).slice(0, 4).map((s) => <Chip key={s} className={classes.chip} size="small" label={s} />)}
                    </div>
                    {t.hourlyRate ? <Typography className={classes.rate}>₹{t.hourlyRate}/hr</Typography> : null}
                    {t.sourceUrl ? (
                      <Link className={classes.source} href={t.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {t.source || 'source'} <OpenInNewIcon style={{ fontSize: 14 }} />
                      </Link>
                    ) : null}
                  </CardContent>

                  <Divider />
                  <div className={classes.actions}>
                    <Tooltip title={hasPin(t) ? 'Open exact location pin' : (hasLocation(t) ? 'Open area on map' : 'No location on file')}>
                      <span>
                        <IconButton
                          size="small"
                          color={hasPin(t) ? 'primary' : 'default'}
                          disabled={!hasLocation(t) || busyId === t.id}
                          onClick={() => window.open(locationUrl(t), '_blank', 'noopener')}
                        >
                          <RoomIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Select
                      className={classes.genderSelect}
                      value={t.gender === 'Female' || t.gender === 'Male' || t.gender === 'Other' ? t.gender : ''}
                      displayEmpty
                      disableUnderline
                      disabled={busyId === t.id}
                      onChange={(e) => handleSetGender(t, e.target.value as 'Female' | 'Male' | 'Other')}
                    >
                      <MenuItem value="" disabled>Set gender</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>

                    <span className={classes.spacer} />

                    <Tooltip title="Delete tutor">
                      <span>
                        <IconButton
                          size="small"
                          style={{ color: '#d32f2f' }}
                          disabled={busyId === t.id}
                          onClick={() => handleDelete(t)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </Card>
              </Grid>
            ))}
            {tutors.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">No mocked teachers match your search.</Typography>
              </Grid>
            )}
          </Grid>

          <div className={classes.pager}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Typography variant="body2">Page {page} / {totalPages}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </>
      )}
    </div>
  );
};

export default MockedTeachersScreen;
